import { useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatoCantidad } from '@/lib/utils/formato-precio';
import type { ValorDeFaceta } from '../types/price-proposals.types';

/**
 * Un filtro de elección múltiple con SÓLO los valores que hay en la bandeja,
 * cada uno con cuántas tiene (lote B1, spec 246 punto 1; maqueta `b1-bandeja`).
 * El valor `null` de la faceta es «sin categoría» / «sin marca»; elegido viaja
 * como '' (el backend lo lee así).
 *
 * El botón dice qué hay puesto: «Marca: todas», «Marca: Sony», «Marca: 2 elegidas».
 */

/** Cómo viaja un valor de la faceta: null → '' («sin»). */
export const claveDelValor = (v: string | null) => v ?? '';

export function FiltroDeValores({
  titulo,
  sinValor,
  buscarUna,
  valores,
  elegidos,
  onCambiar,
  pie,
}: {
  /** «Categoría», «Marca». */
  titulo: string;
  /** Cómo se llama el vacío: «Sin categoría», «Sin marca». */
  sinValor: string;
  /** «Buscar una categoría». */
  buscarUna: string;
  /** null = todavía no llegaron (o no se pudieron traer): el filtro no se ofrece. */
  valores: ValorDeFaceta[] | null;
  /** Las claves elegidas ('' = sin). */
  elegidos: string[];
  onCambiar: (elegidos: string[]) => void;
  /** Lo que va al pie de la lista. */
  pie: string;
}) {
  const [busqueda, setBusqueda] = useState('');
  const nombre = (v: string | null) => v ?? sinValor;
  // Un valor elegido que en esta pestaña no tiene ninguna (vino de otra) se
  // muestra igual, con 0, para poder destildarlo (auditoría del 3-oct).
  const conLosElegidos = [
    ...(valores ?? []),
    ...elegidos
      .filter((e) => !(valores ?? []).some((v) => claveDelValor(v.valor) === e))
      .map((e) => ({ valor: e === '' ? null : e, cuantas: 0 })),
  ];
  // Unas decenas de valores: se filtra en cada pintada, sin memo.
  const t = busqueda.trim().toLocaleLowerCase('es');
  const visibles = conLosElegidos.filter((v) =>
    nombre(v.valor).toLocaleLowerCase('es').includes(t)
  );

  const resumen =
    elegidos.length === 0
      ? 'todas'
      : elegidos.length === 1
        ? elegidos[0] === ''
          ? sinValor.toLocaleLowerCase('es')
          : elegidos[0]
        : `${formatoCantidad(elegidos.length)} elegidas`;

  const alternar = (clave: string) =>
    onCambiar(
      elegidos.includes(clave) ? elegidos.filter((e) => e !== clave) : [...elegidos, clave]
    );

  return (
    <Popover onOpenChange={(abierto) => !abierto && setBusqueda('')}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 font-normal"
          disabled={valores == null}
          aria-label={`${titulo}: ${resumen}`}
        >
          {titulo}:{' '}
          <span className={elegidos.length > 0 ? 'font-semibold' : 'text-muted-foreground'}>
            {resumen}
          </span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0 text-sm">
        <div className="relative border-b p-2">
          <Search
            className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder={buscarUna}
            aria-label={buscarUna}
            className="h-8 pl-8"
          />
        </div>
        <p className="px-3 pb-1 pt-2 text-xs text-muted-foreground">
          Sólo las que hay en tu bandeja
        </p>
        <ul
          role="listbox"
          aria-label={titulo}
          aria-multiselectable="true"
          className="max-h-64 overflow-y-auto pb-1"
        >
          {visibles.length === 0 && (
            <li className="px-3 py-2 text-xs text-muted-foreground">Ninguna con ese nombre.</li>
          )}
          {visibles.map((v) => {
            const clave = claveDelValor(v.valor);
            const elegido = elegidos.includes(clave);
            return (
              // Una opción no lleva una casilla adentro (auditoría del 3-oct): se
              // elige con el clic o con Enter/Espacio, y la marca es sólo dibujo.
              <li
                key={clave}
                role="option"
                aria-selected={elegido}
                tabIndex={0}
                onClick={() => alternar(clave)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  alternar(clave);
                }}
                className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-muted/50 focus:bg-muted/50 focus:outline-none"
              >
                <span
                  aria-hidden="true"
                  className={
                    elegido
                      ? 'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary bg-primary text-primary-foreground'
                      : 'h-4 w-4 shrink-0 rounded-sm border border-primary'
                  }
                >
                  {elegido && <Check className="h-3.5 w-3.5" />}
                </span>
                <span className={v.valor == null ? 'text-muted-foreground' : undefined}>
                  {nombre(v.valor)}
                </span>
                <span className="ml-auto tabular-nums text-xs text-muted-foreground">
                  {formatoCantidad(v.cuantas)}
                </span>
              </li>
            );
          })}
        </ul>
        <p className="border-t px-3 py-2 text-xs text-muted-foreground">{pie}</p>
      </PopoverContent>
    </Popover>
  );
}
